import React, { useState, useMemo } from 'react';
import { base44 } from '@/api/base44Client';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Loader2, Plus, ShieldOff, RefreshCw, CheckCircle2, AlertCircle, X,
  RotateCcw, Square, CheckSquare, Link2, ArrowLeft, Trash2, ChevronUp, ChevronDown, Upload, Barcode,
} from 'lucide-react';
import ProductFormModal from '@/components/admin/ProductFormModal';
import AddProductFromLinkModal from '@/components/admin/AddProductFromLinkModal';
import ProductGroupRow from '@/components/admin/ProductGroupRow';
import SquareCategorySyncModal from '@/components/admin/SquareCategorySyncModal';
import ProductsFilterBar from '@/components/admin/ProductsFilterBar';
import ProductsExportMenu from '@/components/admin/ProductsExportMenu';
import ImagePushResult from '@/components/admin/ImagePushResult';
import BarcodePushResult from '@/components/admin/BarcodePushResult';
import { exportProductsToCsv } from '@/utils/exportProductsCsv';
import { exportProductsToEbayCsv } from '@/utils/exportEbayCsv';
import { exportProductsToLitCommerceCsv } from '@/utils/exportLitCommerceCsv';
import { useAuth } from '@/lib/AuthContext';
import { useToast } from '@/components/ui/use-toast';
import ConfirmDialog from '@/components/admin/ConfirmDialog';

const CATEGORY_LABELS = {
  sim_rigs: 'SIM RIGS', steering_wheels: 'WHEELS', pedals: 'PEDALS',
  motion_platforms: 'MOTION', monitors: 'DISPLAYS', accessories: 'BRACKETS', gaming_computers: 'COMPUTERS',
};

export default function AdminProducts() {
  const { user: currentUser, isLoadingAuth: authLoading } = useAuth();
  const { toast } = useToast();
  const [search, setSearch] = useState('');
  const [modalProduct, setModalProduct] = useState(undefined);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [reimporting, setReimporting] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkReimporting, setBulkReimporting] = useState(false);
  const [syncModal, setSyncModal] = useState(null); // null | 'sync' | 'reimport' | 'reimport-selected'
  const [aiPosting, setAiPosting] = useState(false);
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  // Pre-seed for "Add variation" — opens the product form pre-filled as a new
  // variant of the given parent product.
  const [variationPreset, setVariationPreset] = useState(null);
  // "Send images to Square" — running flag plus the per-item outcome to show.
  const [imagePushing, setImagePushing] = useState(false);
  const [imagePushResult, setImagePushResult] = useState(null);
  // "Apply mount barcodes" — running flag plus the per-colour outcome to show.
  const [barcodePushing, setBarcodePushing] = useState(false);
  const [barcodeResult, setBarcodeResult] = useState(null);
  const queryClient = useQueryClient();

  const { data: products = [], isLoading } = useQuery({
    queryKey: ['admin-products'],
    queryFn: () => base44.entities.Product.list('-created_date', 1000),
    enabled: !!currentUser && currentUser.role === 'admin',
  });

  // Category labels come from the Category entities (source of truth) so an
  // edit in Category Management is reflected here immediately.
  const { data: categories = [] } = useQuery({
    queryKey: ['admin-categories'],
    queryFn: () => base44.entities.Category.list('sort_order', 200),
    enabled: !!currentUser && currentUser.role === 'admin',
  });
  const categoryLabelMap = useMemo(() => {
    const m = {};
    (categories || []).forEach((c) => { if (c.key) m[c.key] = c.label; });
    return m;
  }, [categories]);
  const categoryLabel = (p) => categoryLabelMap[p.category] || CATEGORY_LABELS[p.category] || p.category;

  // Listing filters + sort.
  const [stockFilter, setStockFilter] = useState('all'); // all | in | out
  const [publishedFilter, setPublishedFilter] = useState('all'); // all | published | hidden
  const [manualFilter, setManualFilter] = useState('all'); // all | manual | square
  const [platformFilter, setPlatformFilter] = useState('all'); // all | pc | console | both
  const [sort, setSort] = useState({ field: 'created', dir: 'desc' });

  const showStatus = (type, message, stats = null, categories_used = null, costs = null) => {
    setSyncStatus({ type, message, stats, categories_used, costs });
    // Errors persist until dismissed; success auto-fades.
    if (type === 'success') setTimeout(() => setSyncStatus(null), 7000);
  };

  const handleToggleStock = async (product) => {
    const newInStock = !product.in_stock;
    try {
      await base44.entities.Product.update(product.id, {
        square_item_id: product.square_item_id,
        in_stock: newInStock,
        ...(newInStock && product.stock_quantity === 0 ? { stock_quantity: null } : {}),
      });
      queryClient.invalidateQueries({ queryKey: ['admin-products'] });
    } catch (e) {
      toast({ title: 'Stock update failed', description: e.message, variant: 'destructive' });
    }
  };

  const handleSaved = () => {
    setModalProduct(undefined);
    setVariationPreset(null);
    queryClient.invalidateQueries({ queryKey: ['admin-products'] });
    queryClient.invalidateQueries({ queryKey: ['products'] });
  };

  const closeProductModal = () => {
    setModalProduct(undefined);
    setVariationPreset(null);
  };

  const toggleSelectGroup = (ids, selectAll) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (selectAll) ids.forEach(id => next.add(id));
      else ids.forEach(id => next.delete(id));
      return next;
    });
  };

  // Open the product form pre-seeded as a new variation of `parent`.
  const handleAddVariation = (parent) => {
    setVariationPreset({
      is_variant: true,
      // Manual product bypasses the Square Item ID requirement and protects
      // the new variation from being overwritten/deleted by Square syncs.
      manual_product: true,
      square_parent_item_id: parent.square_parent_item_id || parent.square_item_id || '',
      name: parent.name,
      category: parent.category,
      reporting_category: parent.reporting_category,
      option_name: parent.option_name || 'Colour',
      ebay_variation_aspect: parent.ebay_variation_aspect || 'Colour',
      ebay_group_key: parent.ebay_group_key || parent.square_parent_item_id || parent.square_item_id || '',
      ebay_category_id: parent.ebay_category_id || '',
      published: true,
    });
    setModalProduct(null);
  };

  const handleLinkCreated = () => {
    setLinkModalOpen(false);
    queryClient.invalidateQueries({ queryKey: ['admin-products'] });
    queryClient.invalidateQueries({ queryKey: ['products'] });
  };

  const handleDelete = (product) => setDeleteTarget(product);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await base44.entities.Product.delete(deleteTarget.id);
      queryClient.invalidateQueries({ queryKey: ['admin-products'] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
      setDeleteTarget(null);
    } catch (e) {
      toast({ title: 'Delete failed', description: e.message, variant: 'destructive' });
    }
  };

  const handleBulkDelete = () => {
    if (selected.size === 0) return;
    setBulkDeleteOpen(true);
  };

  const confirmBulkDelete = async () => {
    setBulkDeleting(true);
    try {
      await Promise.all([...selected].map(id => base44.entities.Product.delete(id)));
      setSelected(new Set());
      setBulkDeleteOpen(false);
      queryClient.invalidateQueries({ queryKey: ['admin-products'] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
    } catch (e) {
      toast({ title: 'Bulk delete failed', description: e.message, variant: 'destructive' });
    }
    setBulkDeleting(false);
  };

  // Called when admin confirms category selection in the modal
  const handleSyncConfirm = async ({ categories: selectedCategories, destinations = {} }) => {
    setSyncModal(null);
    if (syncModal === 'sync') {
      setSyncing(true);
      setSyncStatus(null);
      try {
        const res = await base44.functions.invoke('syncSquareProducts', { selected_categories: selectedCategories, category_destinations: destinations });
        const { stats, message, categories_used } = res.data;
        showStatus('success', message, stats, categories_used, res.data.costs);
        queryClient.invalidateQueries({ queryKey: ['admin-products'] });
        queryClient.invalidateQueries({ queryKey: ['products'] });
      } catch (err) {
        showStatus('error', err.message || 'Failed to sync from Square');
      } finally {
        setSyncing(false);
      }
    } else if (syncModal === 'reimport') {
      setReimporting(true);
      setSyncStatus(null);
      try {
        const res = await base44.functions.invoke('reimportSquareProducts', { selected_categories: selectedCategories, category_destinations: destinations });
        const { stats, message, categories_used } = res.data;
        showStatus('success', message, stats, categories_used, res.data.costs);
        queryClient.invalidateQueries({ queryKey: ['admin-products'] });
        queryClient.invalidateQueries({ queryKey: ['products'] });
      } catch (err) {
        showStatus('error', err.message || 'Failed to re-import from Square');
      } finally {
        setReimporting(false);
      }
    } else if (syncModal === 'reimport-selected') {
      const selectedProducts = products.filter(p => selected.has(p.id));
      const squareItemIds = [...new Set(selectedProducts.map(p => p.square_item_id).filter(Boolean))];
      if (squareItemIds.length === 0) {
        showStatus('error', 'No Square IDs found on selected products.');
        return;
      }
      setBulkReimporting(true);
      setSyncStatus(null);
      try {
        const res = await base44.functions.invoke('reimportSquareProducts', { square_item_ids: squareItemIds, selected_categories: selectedCategories, category_destinations: destinations });
        const { stats, message, categories_used } = res.data;
        showStatus('success', message, stats, categories_used, res.data.costs);
        setSelected(new Set());
        queryClient.invalidateQueries({ queryKey: ['admin-products'] });
        queryClient.invalidateQueries({ queryKey: ['products'] });
      } catch (err) {
        showStatus('error', err.message || 'Failed to re-import from Square');
      } finally {
        setBulkReimporting(false);
      }
    }
  };

  const handleSyncSquare = () => setSyncModal('sync');
  const handleReimportAll = () => setSyncModal('reimport');
  const handleReimportSelected = () => {
    if (selected.size === 0) return;
    setSyncModal('reimport-selected');
  };

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleExportEbay = () => {
    const pool = selected.size > 0 ? products.filter(p => selected.has(p.id)) : products;
    const { exported, skipped } = exportProductsToEbayCsv(pool);
    if (exported === 0) {
      showStatus('error', skipped > 0
        ? `No eligible products — ${skipped} skipped (set an eBay category in the product's "eBay Listing" section first).`
        : 'No products to export.');
    } else {
      showStatus('success', `Exported ${exported} product${exported !== 1 ? 's' : ''} to eBay File Exchange CSV${skipped > 0 ? ` (${skipped} skipped — no eBay category)` : ''}.`);
    }
  };

  const handleExportLitCommerce = () => {
    const pool = selected.size > 0 ? products.filter(p => selected.has(p.id)) : products;
    const { exported, skipped } = exportProductsToLitCommerceCsv(pool);
    if (exported === 0) {
      showStatus('error', skipped > 0
        ? `No eligible products — ${skipped} hidden/unpublished items skipped.`
        : 'No products to export.');
    } else {
      showStatus('success', `Exported ${exported} product${exported !== 1 ? 's' : ''} to LitCommerce CSV${skipped > 0 ? ` (${skipped} hidden/unpublished skipped)` : ''}. Upload it as your CSV Main Store in LitCommerce, then publish to channels.`);
    }
  };

  const handleAiPosts = async () => {
    const pool = selected.size > 0
      ? products.filter((p) => selected.has(p.id))
      : products.filter((p) => p.published !== false);
    const ids = pool.map((p) => p.id);
    if (ids.length === 0) {
      showStatus('error', 'No products to generate posts for.');
      return;
    }
    setAiPosting(true);
    try {
      const res = await base44.functions.invoke('createProductSocialDraft', { product_ids: ids });
      const { created, skipped, errors } = res.data;
      showStatus(
        'success',
        `AI generated ${created} social post draft${created !== 1 ? 's' : ''}${skipped?.length ? ` · ${skipped.length} skipped (unpublished)` : ''}${errors?.length ? ` · ${errors.length} error(s)` : ''}. Review them under Social Posts.`,
        { created: created, skipped: skipped?.length || 0, errors }
      );
    } catch (err) {
      showStatus('error', err.message || 'AI post generation failed.');
    } finally {
      setAiPosting(false);
    }
  };

  // Sends packshots up to Square: the selected products if any, otherwise the
  // whole catalogue (accessories and hardware included).
  const handleSendImagesToSquare = async () => {
    setImagePushing(true);
    setImagePushResult(null);
    try {
      const productIds = selected.size > 0 ? products.filter((p) => selected.has(p.id)).map((p) => p.id) : [];
      const res = await base44.functions.invoke('pushProductImagesToSquare', { product_ids: productIds });
      if (res.data?.error) throw new Error(res.data.error);
      setImagePushResult(res.data);
      queryClient.invalidateQueries({ queryKey: ['admin-products'] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
    } catch (err) {
      showStatus('error', err.message || 'Sending images to Square failed.');
    } finally {
      setImagePushing(false);
    }
  };

  // Gives each colour of the in-house Stop Button Mount its own barcode, on the
  // record and on its linked Square variation.
  const handleApplyBarcodes = async () => {
    setBarcodePushing(true);
    setBarcodeResult(null);
    try {
      const res = await base44.functions.invoke('applyStopButtonBarcodes', {});
      if (res.data?.error) throw new Error(res.data.error);
      setBarcodeResult(res.data);
      queryClient.invalidateQueries({ queryKey: ['admin-products'] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
    } catch (err) {
      showStatus('error', err.message || 'Applying the mount barcodes failed.');
    } finally {
      setBarcodePushing(false);
    }
  };

  const [pushingId, setPushingId] = useState(null);

  const handlePushToSquare = async (product) => {
    setPushingId(product.id);
    try {
      const res = await base44.functions.invoke('pushProductToSquare', { product_id: product.id });
      if (res?.error) throw new Error(res.error);
      if (res.action === 'cost_updated') {
        showStatus('success', `Sent the cost price for "${product.name}" to Square.`);
      } else {
        const verb = res.action === 'updated' ? 'Updated' : 'Created';
        showStatus('success', `${verb} "${product.name}" in Square — it will now flow to LitCommerce on the next sync.`);
      }
      queryClient.invalidateQueries({ queryKey: ['admin-products'] });
    } catch (err) {
      showStatus('error', err.message || 'Push to Square failed.');
    } finally {
      setPushingId(null);
    }
  };

  const toggleSelectAll = () => {
    if (selected.size === filtered.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filtered.map(p => p.id)));
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#0d0d0f] pt-16 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!currentUser || currentUser.role !== 'admin') {
    return (
      <div className="min-h-screen bg-[#0d0d0f] pt-16 flex items-center justify-center">
        <div className="text-center">
          <ShieldOff className="w-12 h-12 text-white/10 mx-auto mb-4" />
          <p className="text-white/40 text-sm font-bold tracking-widest">ACCESS DENIED</p>
        </div>
      </div>
    );
  }

  const filtered = products.filter((p) => {
    const q = search.toLowerCase();
    const matchesText = !q ||
      p.name?.toLowerCase().includes(q) ||
      p.category?.toLowerCase().includes(q) ||
      p.sku?.toLowerCase().includes(q) ||
      p.reporting_category?.toLowerCase().includes(q);
    const out = p.in_stock === false || (p.stock_quantity != null && p.stock_quantity <= 0);
    const matchesStock = stockFilter === 'all' || (stockFilter === 'in' && !out) || (stockFilter === 'out' && out);
    const matchesPublished = publishedFilter === 'all' || (publishedFilter === 'published' && p.published !== false) || (publishedFilter === 'hidden' && p.published === false);
    const matchesManual = manualFilter === 'all' || (manualFilter === 'manual' && p.manual_product) || (manualFilter === 'square' && !p.manual_product);
    const matchesPlatform = platformFilter === 'all' || p.platform === platformFilter;
    return matchesText && matchesStock && matchesPublished && matchesManual && matchesPlatform;
  });

  // Group variant records that share a Square parent item token into one
  // expandable row, so a product with colour/option variants shows as a single
  // listing instead of duplicate rows. Standalone products form their own group.
  const grouped = (() => {
    const map = new Map();
    for (const p of filtered) {
      const key = p.is_variant && p.square_parent_item_id ? 'v:' + p.square_parent_item_id : 's:' + p.id;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(p);
    }
    const groups = [];
    for (const [key, members] of map) {
      // Sort so the black/primary variant becomes the parent (first), with the
      // remaining colours (Red, etc.) shown underneath when expanded.
      const sorted = [...members].sort((a, b) => {
        const la = (a.option_value || a.variation_name || a.name || '').toLowerCase();
        const lb = (b.option_value || b.variation_name || b.name || '').toLowerCase();
        const aBlack = la.includes('black');
        const bBlack = lb.includes('black');
        if (aBlack !== bBlack) return aBlack ? -1 : 1;
        return 0;
      });
      const parent = sorted.find(m => !m.is_variant) || sorted[0];
      const variants = sorted.filter(m => m.id !== parent.id);
      groups.push({ key, parent, members: sorted, variants, isGroup: variants.length > 0 });
    }
    return groups;
  })();

  // Sort groups by the chosen column (applied to the group's parent record).
  grouped.sort((a, b) => {
    const pa = a.parent, pb = b.parent;
    let cmp = 0;
    if (sort.field === 'name') cmp = (pa.name || '').localeCompare(pb.name || '');
    else if (sort.field === 'price') cmp = (Number(pa.price) || 0) - (Number(pb.price) || 0);
    else if (sort.field === 'stock') cmp = (Number(pa.stock_quantity ?? 1e9)) - (Number(pb.stock_quantity ?? 1e9));
    else cmp = new Date(pa.created_date || 0).getTime() - new Date(pb.created_date || 0).getTime();
    return sort.dir === 'asc' ? cmp : -cmp;
  });

  const toggleSort = (field) => setSort((s) => (s.field === field ? { field, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { field, dir: 'desc' }));

  const allSelected = filtered.length > 0 && selected.size === filtered.length;

  return (
    <div className="min-h-screen bg-[#0d0d0f] pt-16">
      {modalProduct !== undefined && (
        <ProductFormModal
          product={modalProduct}
          preset={variationPreset}
          onClose={closeProductModal}
          onSaved={handleSaved}
        />
      )}
      {linkModalOpen && (
        <AddProductFromLinkModal
          onClose={() => setLinkModalOpen(false)}
          onCreated={handleLinkCreated}
        />
      )}
      {syncModal && (
        <SquareCategorySyncModal
          mode={syncModal === 'sync' ? 'sync' : 'reimport'}
          onClose={() => setSyncModal(null)}
          onConfirm={handleSyncConfirm}
        />
      )}

      {/* Header */}
      <section className="py-14 px-6 lg:px-10 border-b border-white/5">
        <div className="max-w-6xl mx-auto">
          <Link to="/admin/dashboard" className="inline-flex items-center gap-2 text-white/40 hover:text-white text-xs font-bold tracking-wider mb-4 transition-colors">
            <ArrowLeft className="w-4 h-4" /> BACK TO DASHBOARD
          </Link>
          <div className="flex items-end justify-between gap-4 flex-wrap mb-4">
            <div>
              <p className="text-primary text-xs font-bold tracking-widest mb-2">PRODUCTS &amp; CATALOG</p>
              <h1 className="text-white font-black text-2xl sm:text-3xl md:text-4xl uppercase">PRODUCT MANAGEMENT</h1>
              <p className="text-white/30 text-sm mt-1">{products.length} product{products.length !== 1 ? 's' : ''}</p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <ProductsExportMenu
                onExportCsv={() => exportProductsToCsv(products)}
                onExportEbay={handleExportEbay}
                onExportLitCommerce={handleExportLitCommerce}
                onAiPosts={handleAiPosts}
                aiPosting={aiPosting}
                disabled={products.length === 0}
              />
              <button
                onClick={handleSendImagesToSquare}
                disabled={imagePushing}
                className="flex items-center gap-2 bg-emerald-700 hover:bg-emerald-600 disabled:opacity-50 text-white font-bold text-xs tracking-wider px-4 py-3 transition-colors"
                title={selected.size > 0
                  ? `Send the images of ${selected.size} selected product(s) to Square`
                  : 'Sweep the whole catalogue — send every product image to Square'}
              >
                {imagePushing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {selected.size > 0 ? `SEND ${selected.size} IMAGE${selected.size !== 1 ? 'S' : ''}` : 'SEND IMAGES TO SQUARE'}
              </button>
              <button
                onClick={handleApplyBarcodes}
                disabled={barcodePushing}
                className="flex items-center gap-2 bg-amber-700 hover:bg-amber-600 disabled:opacity-50 text-white font-bold text-xs tracking-wider px-4 py-3 transition-colors"
                title="Set a unique EAN-13 on each colour of the Stop Button Mount and send it to Square"
              >
                {barcodePushing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Barcode className="w-4 h-4" />}
                APPLY MOUNT BARCODES
              </button>
              <button
                onClick={handleReimportAll}
                disabled={reimporting}
                className="flex items-center gap-2 bg-indigo-700 hover:bg-indigo-600 disabled:opacity-50 text-white font-bold text-xs tracking-wider px-4 py-3 transition-colors"
                title="Re-import all products from Square (restores deleted items)"
              >
                {reimporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
                RE-IMPORT ALL
              </button>
              <button
                onClick={handleSyncSquare}
                disabled={syncing}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white font-bold text-xs tracking-wider px-4 py-3 transition-colors"
              >
                {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                SYNC SQUARE
              </button>
              <button
                onClick={() => setLinkModalOpen(true)}
                className="flex items-center gap-2 bg-teal-700 hover:bg-teal-600 text-white font-bold text-xs tracking-wider px-4 py-3 transition-colors"
                title="Generate a new product from a supplier URL and push it to Square"
              >
                <Link2 className="w-4 h-4" /> ADD FROM LINK
              </button>
              <button
                onClick={() => setModalProduct(null)}
                className="flex items-center gap-2 bg-primary hover:bg-primary/90 text-white font-bold text-xs tracking-wider px-4 py-3 transition-colors"
              >
                <Plus className="w-4 h-4" /> ADD PRODUCT
              </button>
            </div>
          </div>

          {/* Sync status */}
          {syncStatus && (
            <div className={`flex items-start gap-3 px-4 py-3 rounded-sm ${
              syncStatus.type === 'success' ? 'bg-green-500/10 border border-green-500/30' : 'bg-red-500/10 border border-red-500/30'
            }`}>
              {syncStatus.type === 'success'
                ? <CheckCircle2 className="w-5 h-5 text-green-400 mt-0.5 shrink-0" />
                : <AlertCircle className="w-5 h-5 text-red-400 mt-0.5 shrink-0" />
              }
              <div>
                <p className={`font-bold text-sm ${syncStatus.type === 'success' ? 'text-green-300' : 'text-red-300'}`}>
                  {syncStatus.message}
                </p>
                {syncStatus.stats && (
                  <p className="text-xs mt-1 text-white/40">
                    Created: {syncStatus.stats.created} · Updated/Restored: {syncStatus.stats.restored ?? syncStatus.stats.updated} · Skipped: {syncStatus.stats.skipped ?? 0} · Errors: {syncStatus.stats.errors?.length ?? 0}
                  </p>
                )}
                {syncStatus.costs?.pushed > 0 && (
                  <p className="text-xs mt-0.5 text-white/25">
                    Cost prices sent to Square: {syncStatus.costs.pushed}
                    {syncStatus.costs.failed ? ` · ${syncStatus.costs.failed} refused` : ''}
                    {syncStatus.costs.skipped ? ` · ${syncStatus.costs.skipped} skipped (no cost recorded, or no Square variation)` : ''}
                  </p>
                )}
                {syncStatus.categories_used?.length > 0 && (
                  <p className="text-xs mt-0.5 text-white/25">
                    Categories: {syncStatus.categories_used.join(', ')}
                  </p>
                )}
              </div>
              <button onClick={() => setSyncStatus(null)} className="ml-auto text-white/30 hover:text-white p-1 shrink-0" title="Dismiss">
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          <ImagePushResult result={imagePushResult} onClose={() => setImagePushResult(null)} />
          <BarcodePushResult result={barcodeResult} onClose={() => setBarcodeResult(null)} />
        </div>
      </section>

      <section className="px-6 lg:px-10 py-10 max-w-6xl mx-auto">
        <ProductsFilterBar
          search={search} setSearch={setSearch}
          stock={stockFilter} setStock={setStockFilter}
          published={publishedFilter} setPublished={setPublishedFilter}
          manual={manualFilter} setManual={setManualFilter}
          platform={platformFilter} setPlatform={setPlatformFilter}
        />

        {selected.size > 0 && (
          <div className="flex items-center gap-2 mb-6">
            <span className="text-white/40 text-xs">{selected.size} selected</span>
            <button
              onClick={handleReimportSelected}
              disabled={bulkReimporting}
              className="flex items-center gap-1.5 bg-indigo-700 hover:bg-indigo-600 disabled:opacity-50 text-white font-bold text-xs px-3 py-2 transition-colors"
            >
              {bulkReimporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
              RE-IMPORT
            </button>
            <button
              onClick={handleBulkDelete}
              disabled={bulkDeleting}
              className="flex items-center gap-1.5 bg-red-700 hover:bg-red-600 disabled:opacity-50 text-white font-bold text-xs px-3 py-2 transition-colors"
            >
              {bulkDeleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
              DELETE
            </button>
          </div>
        )}

        {isLoading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 border border-white/5 bg-[#111115]">
            <p className="text-white/30 text-sm">No products found.</p>
          </div>
        ) : (
          <div className="border border-white/5 overflow-hidden">
            <div className="hidden sm:grid grid-cols-12 gap-4 px-6 py-3 bg-[#111115] border-b border-white/5">
              <div className="col-span-1 flex items-center">
                <button onClick={toggleSelectAll} className="text-white/30 hover:text-white transition-colors">
                  {allSelected ? <CheckSquare className="w-4 h-4 text-primary" /> : <Square className="w-4 h-4" />}
                </button>
              </div>
              <button onClick={() => toggleSort('name')} className="col-span-4 flex items-center gap-1 text-white/30 hover:text-white text-[10px] font-bold tracking-widest">
                PRODUCT
                {sort.field === 'name' && (sort.dir === 'asc' ? <ChevronUp className="w-3 h-3 text-primary" /> : <ChevronDown className="w-3 h-3 text-primary" />)}
              </button>
              <p className="col-span-2 text-white/30 text-[10px] font-bold tracking-widest">CATEGORY</p>
              <button onClick={() => toggleSort('price')} className="col-span-2 flex items-center gap-1 text-white/30 hover:text-white text-[10px] font-bold tracking-widest">
                PRICE
                {sort.field === 'price' && (sort.dir === 'asc' ? <ChevronUp className="w-3 h-3 text-primary" /> : <ChevronDown className="w-3 h-3 text-primary" />)}
              </button>
              <button onClick={() => toggleSort('stock')} className="col-span-1 flex items-center gap-1 text-white/30 hover:text-white text-[10px] font-bold tracking-widest">
                STOCK
                {sort.field === 'stock' && (sort.dir === 'asc' ? <ChevronUp className="w-3 h-3 text-primary" /> : <ChevronDown className="w-3 h-3 text-primary" />)}
              </button>
              <p className="col-span-2 text-white/30 text-[10px] font-bold tracking-widest">ACTIONS</p>
            </div>

            {grouped.map((group) => (
              <ProductGroupRow
                key={group.key}
                group={group}
                categoryLabel={categoryLabel}
                selected={selected}
                onToggleSelect={toggleSelect}
                onToggleSelectGroup={toggleSelectGroup}
                onEdit={(p) => setModalProduct(p)}
                onDelete={handleDelete}
                onToggleStock={handleToggleStock}
                onPush={handlePushToSquare}
                pushingId={pushingId}
                onAddVariation={handleAddVariation}
              />
            ))}
          </div>
        )}
      </section>

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete product"
        description={deleteTarget ? `Delete "${deleteTarget.name}"? This removes it from the storefront. You can re-import it from Square at any time.` : ''}
        confirmLabel="Delete"
        onConfirm={confirmDelete}
        onClose={() => setDeleteTarget(null)}
      />
      <ConfirmDialog
        open={bulkDeleteOpen}
        title="Delete selected products"
        description={`Delete ${selected.size} selected product(s)? You can re-import them from Square at any time.`}
        confirmLabel="Delete"
        onConfirm={confirmBulkDelete}
        onClose={() => setBulkDeleteOpen(false)}
      />
    </div>
  );
}